const STORAGE_KEY = '2087-save-v1';

const DEFAULT_SAVE = {
  levels: {
    // levelId: { unlocked, bestTimeMs, bestScore, upgrades: [] }
  },
  settings: {
    musicVolume: 0.6,
    sfxVolume: 0.8
  }
};

function loadRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return structuredClone(DEFAULT_SAVE);
    const parsed = JSON.parse(raw);
    return {
      levels: parsed.levels ?? {},
      settings: { ...DEFAULT_SAVE.settings, ...(parsed.settings ?? {}) }
    };
  } catch (err) {
    console.warn('[SaveManager] Corrupt save data, resetting.', err);
    return structuredClone(DEFAULT_SAVE);
  }
}

class SaveManagerImpl {
  constructor() {
    this.data = loadRaw();
  }

  persist() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
  }

  /** Ensures level entries exist and applies `unlockedByDefault` from levels.json. */
  syncWithLevelDefs(levelDefs) {
    let changed = false;
    for (const [id, def] of Object.entries(levelDefs)) {
      if (!this.data.levels[id]) {
        this.data.levels[id] = {
          unlocked: !!def.unlockedByDefault,
          bestTimeMs: null,
          bestScore: 0,
          bestChips: 0,
          chipsTotal: 0,
          upgrades: []
        };
        changed = true;
      }
    }
    if (changed) this.persist();
  }

  isUnlocked(levelId) {
    return !!this.data.levels[levelId]?.unlocked;
  }

  unlock(levelId) {
    if (!this.data.levels[levelId]) {
      this.data.levels[levelId] = {
        unlocked: true,
        bestTimeMs: null,
        bestScore: 0,
        bestChips: 0,
        chipsTotal: 0,
        upgrades: []
      };
    } else {
      this.data.levels[levelId].unlocked = true;
    }
    this.persist();
  }

  recordResult(levelId, { timeMs, score, chipsCollected, chipsTotal, upgrades } = {}) {
    const entry = this.data.levels[levelId] ?? {
      unlocked: true,
      bestTimeMs: null,
      bestScore: 0,
      bestChips: 0,
      chipsTotal: 0,
      upgrades: []
    };
    entry.unlocked = true;
    if (typeof timeMs === 'number' && (entry.bestTimeMs === null || timeMs < entry.bestTimeMs)) {
      entry.bestTimeMs = timeMs;
    }
    if (typeof score === 'number' && score > entry.bestScore) {
      entry.bestScore = score;
    }
    if (typeof chipsCollected === 'number' && chipsCollected > (entry.bestChips ?? 0)) {
      entry.bestChips = chipsCollected;
    }
    if (typeof chipsTotal === 'number') {
      entry.chipsTotal = chipsTotal;
    }
    if (Array.isArray(upgrades)) {
      entry.upgrades = Array.from(new Set([...(entry.upgrades ?? []), ...upgrades]));
    }
    this.data.levels[levelId] = entry;
    this.persist();
    return entry;
  }

  getLevelState(levelId) {
    return (
      this.data.levels[levelId] ?? {
        unlocked: false,
        bestTimeMs: null,
        bestScore: 0,
        bestChips: 0,
        chipsTotal: 0,
        upgrades: []
      }
    );
  }

  getSetting(key) {
    return this.data.settings[key];
  }

  setSetting(key, value) {
    this.data.settings[key] = value;
    this.persist();
  }

  resetAll() {
    this.data = structuredClone(DEFAULT_SAVE);
    this.persist();
  }
}

export const SaveManager = new SaveManagerImpl();
